<section class="estimate-balance" data-testid="estimate-balance">
  <mmx-breadcrumbs *ngIf="!embedded" [breadcrumbs]="[patient, 'common.estimate']"></mmx-breadcrumbs>

  <ng-container *ngIf="vm() as vm" [ngSwitch]="vm.loadingState">
    <p
      *ngSwitchCase="'loading'"
      class="estimate-balance__loading text-center py-5"
    >
      {{ 'balance.loading' | translate }}
    </p>

    <div *ngSwitchCase="'error'" class="estimate-balance__error text-center py-5">
      <p>{{ (vm.error || 'balance.error') | translate }}</p>
      <button
        mat-raised-button
        type="button"
        class="estimate-balance__retry"
        (click)="retry()"
      >
        {{ 'balance.retry' | translate }}
      </button>
    </div>

    <p *ngSwitchCase="'empty'" class="estimate-balance__empty">{{ 'balance.empty' | translate }}</p>

    <ng-container *ngSwitchDefault>
      <ng-container *ngIf="shouldDelegateToModifySession(vm); else draftSurface">
        <mmx-estimate-modify-session
          [vm]="vm"
          [busy]="busy"
          [estimateForm]="estimateForm"
          [benefitsForm]="benefitsForm"
          [appointmentOptions]="vm.appointmentOptions"
          (modify)="onStartModify()"
          (discard)="onDiscardModify()"
          (finalize)="onFinalize()"
          (calculate)="onCalculate()"
          (viewHistory)="onViewHistory()"
          (void)="onDelete()"
          (send)="onSendLetter($event)"
          (sendDocument)="onSendDocument()"
          (printLetter)="onPrintLetter()"
          (chargesChanged)="onChargesChanged()"
          (chargesReset)="onChargesReset($event)"
          (templateSelected)="onTemplateSelected($event)"
          (appointmentSelectionChange)="onAppointmentSelectionChange($event)"
          (cocModeChange)="onCocModeChange($event)"
          (unscheduledChange)="onUnscheduledChange($event)"
          (addDiscount)="onAddDiscount($event)"
          (removeDiscount)="onRemoveDiscount($event)"
          (benefitsChanged)="onBenefitsChanged()"
          (primaryInsuranceChange)="onPrimaryInsuranceChange($event)"
          (secondaryInsuranceChange)="onSecondaryInsuranceChange($event)"
          (pbTrace)="onPbTrace($event)"
          (hbTrace)="onHbTrace($event)"
          (chargeBreakdown)="onChargeBreakdown($event)"
        ></mmx-estimate-modify-session>
      </ng-container>

      <ng-template #draftSurface>
        <ng-container *ngIf="vm.showInputs">
          <mmx-balance-charges-panel
            [estimate]="vm.estimate"
            [estimateForm]="estimateForm"
            [appointmentOptions]="vm.appointmentOptions"
            [claimSearchEnabled]="vm.claimSearchEnabled"
            [claimSearchPrefillQuery]="vm.claimSearchPrefillQuery"
            [patientType]="vm.patientType"
            [cocMode]="vm.cocMode"
            [unscheduledSelected]="vm.unscheduled"
            [showMcsResponse]="showMcsResponseButton()"
            [busy]="busy"
            (reset)="onChargesReset($event)"
            (templateSelected)="onTemplateSelected($event)"
            (appointmentSelectionChange)="onAppointmentSelectionChange($event)"
            (cocModeChange)="onCocModeChange($event)"
            (unscheduledChange)="onUnscheduledChange($event)"
            (mcsResponse)="onMcsResponse()"
            (calculate)="onCalculate()"
            (addDiscountControl)="onAddDiscount($event)"
            (removeDiscountControl)="onRemoveDiscount($event)"
            (chargesChanged)="onChargesChanged()"
            (claimAdded)="onClaimAdded($event)">
          </mmx-balance-charges-panel>

          <mmx-insurances-card [visitAppointment]="vm.visitAppointment" [visitAppointments]="vm.visitAppointments"></mmx-insurances-card>

          <mmx-balance-benefits-panel
            [form]="benefitsForm"
            [primaryLoading]="vm.primaryBenefitsLoading"
            [secondaryLoading]="vm.secondaryBenefitsLoading"
            [originalPrimary]="vm.estimate?.originalBenefits ?? null"
            [originalSecondary]="vm.estimate?.originalSecondaryBenefits ?? null"
            [hasSecondaryInsurance]="vm.hasSecondaryInsurance"
            [primaryInsuranceOptions]="vm.primaryInsuranceOptions"
            [secondaryInsuranceOptions]="vm.secondaryInsuranceOptions"
            [primaryInsurance]="vm.primaryInsurance"
            [insuranceSelectionResolved]="vm.insuranceSelectionResolved"
            [secondaryInsurance]="vm.secondaryInsurance"
            (benefitsChanged)="onBenefitsChanged()"
            (primaryInsuranceChange)="onPrimaryInsuranceChange($event)"
            (secondaryInsuranceChange)="onSecondaryInsuranceChange($event)">
          </mmx-balance-benefits-panel>
        </ng-container>

        <mmx-balance-charity-care-section
          [status]="vm.patientCharityCareStatus"
          [timezone]="vm.charityCareTimezone"
          [visible]="vm.charityCareVisible"
          [loading]="(patientData.charityScreeningLoading$ | async) ?? false"
          [patientId]="patient?.id ?? null">
        </mmx-balance-charity-care-section>

        <mmx-estimate-review-multi-visit
          [vm]="vm.multiVisitReview"
          [busy]="busy"
          [summaryData]="vm.summaryData"
          [summaryCapabilities]="vm.summaryCapabilities"
          [needsPricing]="vm.needsPricing"
          (finalize)="onFinalize()"
          (save)="onSave()"
          (revert)="onRevert()"
          (viewHistory)="onViewHistory()"
          (delete)="onDelete()"
          (send)="onSendLetter($event)"
          (sendDocument)="onSendDocument()"
          (printLetter)="onPrintLetter()"
          (pbTrace)="onPbTrace($event)"
          (hbTrace)="onHbTrace($event)"
          (chargeBreakdown)="onChargeBreakdown($event)"
        ></mmx-estimate-review-multi-visit>
      </ng-template>

      <ng-container *mmxHasAccess="'payment'">
        <mmx-balance-collect-payment-card
          *ngIf="showCollectPaymentCard(vm.balance)"
          [balance]="vm.balance ?? null"
          [clinic]="clinic"
          [patient]="patient"
          [busy]="busy"
          (paymentApplied)="onPaymentApplied($event)"
          (paymentPlanCreated)="onPaymentPlanCreated($event)"
          (paymentPlanCanceled)="onPaymentPlanCanceled($event)"
        ></mmx-balance-collect-payment-card>

        <mmx-balance-transactions-card
          *ngIf="showTransactionsCard(vm.balance)"
          [balanceId]="vm.balance?.id ?? null"
          (refundRequested)="onRefundRequested($event)"
        ></mmx-balance-transactions-card>
      </ng-container>
    </ng-container>
  </ng-container>
</section>
